import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Divider, Checkbox,
} from '@mui/material';
import {
  Receipt, Visibility, Close, Publish, Add, Delete, Print,
  CheckCircle, Schedule,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

function fmt(n: number | string | null | undefined, currency = 'HKD') {
  if (n == null || n === '') return '—';
  return `${currency} ${Number(n).toLocaleString('en-HK', { minimumFractionDigits: 2 })}`;
}
function fmtDate(d: string | null | undefined) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'long', year: 'numeric' });
}

interface PayslipItem { description: string; amount: number; }
interface Payslip { id: string; employeeId: string; payrollRunId?: string; periodStartDate: string; periodEndDate: string; payDate?: string; periodName?: string; basicSalary: number; totalAllowances: number; totalBonus: number; totalDeductions: number; grossPay: number; netPay: number; currency: string; earnings?: PayslipItem[]; deductions?: PayslipItem[]; viewedAt?: string; isPublished: boolean; employee?: { firstName: string; lastName: string; employeeId: string; department?: string; jobTitle?: string }; }
interface Stats { count: number; totalNetYTD: number; totalDeductionsYTD: number; latestNetPay: number | null; latestPeriod: string | null; }

function PayslipDetail({ payslip, onClose }: { payslip: Payslip; onClose: () => void }) {
  const emp = payslip.employee;
  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt />
          <Box>
            <Typography fontWeight={700}>Payslip — {payslip.periodName ?? `${payslip.periodStartDate} to ${payslip.periodEndDate}`}</Typography>
            {emp && <Typography variant="caption" color="text.secondary">{emp.firstName} {emp.lastName} · {emp.jobTitle}</Typography>}
          </Box>
        </Box>
        <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Header info */}
        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 6 }}><Typography variant="caption" color="text.secondary">Pay Period</Typography><Typography variant="body2" fontWeight={600}>{fmtDate(payslip.periodStartDate)} – {fmtDate(payslip.periodEndDate)}</Typography></Grid>
            <Grid size={{ xs: 6 }}><Typography variant="caption" color="text.secondary">Pay Date</Typography><Typography variant="body2" fontWeight={600}>{fmtDate(payslip.payDate)}</Typography></Grid>
            {emp && <>
              <Grid size={{ xs: 6 }}><Typography variant="caption" color="text.secondary">Employee ID</Typography><Typography variant="body2">{emp.employeeId}</Typography></Grid>
              <Grid size={{ xs: 6 }}><Typography variant="caption" color="text.secondary">Department</Typography><Typography variant="body2">{emp.department ?? '—'}</Typography></Grid>
            </>}
          </Grid>
        </Box>

        {/* Earnings */}
        <Typography fontWeight={700} sx={{ mb: 1 }}>Earnings</Typography>
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell>Basic Salary</TableCell>
                <TableCell align="right"><b>{fmt(payslip.basicSalary, payslip.currency)}</b></TableCell>
              </TableRow>
              {payslip.earnings?.filter(e => e.description !== 'Basic Salary').map((e, i) => (
                <TableRow key={i}><TableCell>{e.description}</TableCell><TableCell align="right">{fmt(e.amount, payslip.currency)}</TableCell></TableRow>
              ))}
              <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                <TableCell><b>Gross Pay</b></TableCell>
                <TableCell align="right"><b>{fmt(payslip.grossPay, payslip.currency)}</b></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Deductions */}
        <Typography fontWeight={700} sx={{ mb: 1 }}>Deductions</Typography>
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableBody>
              {payslip.deductions?.map((d, i) => (
                <TableRow key={i}><TableCell>{d.description}</TableCell><TableCell align="right">({fmt(d.amount, payslip.currency)})</TableCell></TableRow>
              ))}
              {(!payslip.deductions || payslip.deductions.length === 0) && (
                <TableRow><TableCell colSpan={2} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No deductions</TableCell></TableRow>
              )}
              <TableRow sx={{ bgcolor: '#fce4ec' }}>
                <TableCell><b>Total Deductions</b></TableCell>
                <TableCell align="right"><b>({fmt(payslip.totalDeductions, payslip.currency)})</b></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />

        {/* Net pay */}
        <Box sx={{ bgcolor: '#1a237e', color: '#fff', p: 2, borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>Net Pay</Typography>
          <Typography variant="h5" fontWeight={900}>{fmt(payslip.netPay, payslip.currency)}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<Print />} onClick={() => window.print()}>Print</Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PayslipViewer() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const [tab, setTab] = useState(0);
  const [myPayslips, setMyPayslips] = useState<Payslip[]>([]);
  const [allPayslips, setAllPayslips] = useState<Payslip[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [viewPayslip, setViewPayslip] = useState<Payslip | null>(null);
  const [createDialog, setCreateDialog] = useState<Partial<Payslip> | null>(null);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [my, st] = await Promise.all([
        apiFetch('/api/payslips/my'),
        apiFetch('/api/payslips/stats'),
      ]);
      setMyPayslips(my || []); setStats(st);
      if (isHR) {
        const all = await apiFetch(`/api/payslips?year=${yearFilter}`);
        setAllPayslips(all || []);
      }
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [isHR, yearFilter]);

  useEffect(() => { load(); }, [load]);

  const viewSlip = async (ps: Payslip, mine: boolean) => {
    const full = await apiFetch(mine ? `/api/payslips/my/${ps.id}` : `/api/payslips/${ps.id}`).catch(() => ps);
    setViewPayslip(full);
  };

  const publish = async (id: string) => {
    try { await apiFetch(`/api/payslips/${id}/publish`, { method: 'PATCH' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const bulkPublish = async () => {
    try { await apiFetch('/api/payslips/bulk-publish', { method: 'POST', body: JSON.stringify({ ids: Array.from(selected) }) }); setSelected(new Set()); load(); }
    catch (e: any) { setError(e.message); }
  };

  const deleteSlip = async (id: string) => {
    if (!window.confirm('Delete payslip?')) return;
    try { await apiFetch(`/api/payslips/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const save = async () => {
    if (!createDialog) return;
    try { await apiFetch('/api/payslips', { method: 'POST', body: JSON.stringify(createDialog) }); setCreateDialog(null); load(); }
    catch (e: any) { setError(e.message); }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const years = ['2024', '2025', '2026', '2027'];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Payslips</Typography>
            <Typography variant="body2" color="text.secondary">View and download your payslips</Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#1a237e' }}
            onClick={() => setCreateDialog({ currency: 'HKD', isPublished: false, totalAllowances: 0, totalBonus: 0, totalDeductions: 0 })}>
            Create Payslip
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1, bgcolor: '#1a237e', color: '#fff' }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700}>{stats.latestNetPay ? `HK$${Math.round(stats.latestNetPay / 1000)}k` : '—'}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Latest Net Pay</Typography>
                {stats.latestPeriod && <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>{stats.latestPeriod}</Typography>}
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#2e7d32">{stats.count}</Typography>
                <Typography variant="caption" color="text.secondary">Payslips This Year</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#1a237e">{`HK$${Math.round(stats.totalNetYTD / 1000)}k`}</Typography>
                <Typography variant="caption" color="text.secondary">Net Pay YTD</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#c62828">{`HK$${Math.round(stats.totalDeductionsYTD / 1000)}k`}</Typography>
                <Typography variant="caption" color="text.secondary">Deductions YTD</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="My Payslips" />
          {isHR && <Tab label="All Payslips (HR)" />}
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── MY PAYSLIPS ── */}
      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Period</b></TableCell>
                <TableCell><b>Pay Date</b></TableCell>
                <TableCell align="right"><b>Basic</b></TableCell>
                <TableCell align="right"><b>Gross</b></TableCell>
                <TableCell align="right"><b>Net Pay</b></TableCell>
                <TableCell><b>Viewed</b></TableCell>
                <TableCell><b>View</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {myPayslips.map(ps => (
                <TableRow key={ps.id} hover>
                  <TableCell><Typography fontWeight={600}>{ps.periodName ?? ps.periodStartDate}</Typography></TableCell>
                  <TableCell>{fmtDate(ps.payDate)}</TableCell>
                  <TableCell align="right">{fmt(ps.basicSalary, ps.currency)}</TableCell>
                  <TableCell align="right">{fmt(ps.grossPay, ps.currency)}</TableCell>
                  <TableCell align="right"><Typography fontWeight={700} color="primary">{fmt(ps.netPay, ps.currency)}</Typography></TableCell>
                  <TableCell>
                    {ps.viewedAt
                      ? <Tooltip title={new Date(ps.viewedAt).toLocaleString('en-HK')}><CheckCircle fontSize="small" color="success" /></Tooltip>
                      : <Schedule fontSize="small" color="disabled" />}
                  </TableCell>
                  <TableCell>
                    <Button size="small" startIcon={<Visibility />} onClick={() => viewSlip(ps, true)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
              {myPayslips.length === 0 && (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No payslips available yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── ALL PAYSLIPS (HR) ── */}
      {tab === 1 && isHR && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField label="Year" select size="small" value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
              {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
            {selected.size > 0 && (
              <Button variant="contained" color="success" startIcon={<Publish />} onClick={bulkPublish}>
                Publish {selected.size} Selected
              </Button>
            )}
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell padding="checkbox"><Checkbox indeterminate={selected.size > 0 && selected.size < allPayslips.length} checked={selected.size === allPayslips.length && allPayslips.length > 0} onChange={e => setSelected(e.target.checked ? new Set(allPayslips.map(p => p.id)) : new Set())} /></TableCell>
                  <TableCell><b>Employee</b></TableCell>
                  <TableCell><b>Period</b></TableCell>
                  <TableCell align="right"><b>Net Pay</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Viewed</b></TableCell>
                  <TableCell><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allPayslips.map(ps => (
                  <TableRow key={ps.id} hover selected={selected.has(ps.id)}>
                    <TableCell padding="checkbox"><Checkbox checked={selected.has(ps.id)} onChange={() => toggleSelect(ps.id)} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{ps.employee?.firstName} {ps.employee?.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">{ps.employee?.employeeId}</Typography>
                    </TableCell>
                    <TableCell>{ps.periodName ?? ps.periodStartDate}</TableCell>
                    <TableCell align="right"><Typography fontWeight={700}>{fmt(ps.netPay, ps.currency)}</Typography></TableCell>
                    <TableCell>
                      <Chip label={ps.isPublished ? 'Published' : 'Draft'} size="small" color={ps.isPublished ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>
                      {ps.viewedAt ? <CheckCircle fontSize="small" color="success" /> : <Schedule fontSize="small" color="disabled" />}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View"><IconButton size="small" onClick={() => viewSlip(ps, false)}><Visibility fontSize="small" /></IconButton></Tooltip>
                        {!ps.isPublished && <Tooltip title="Publish"><IconButton size="small" color="success" onClick={() => publish(ps.id)}><Publish fontSize="small" /></IconButton></Tooltip>}
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteSlip(ps.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {allPayslips.length === 0 && (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No payslips for {yearFilter}.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── PAYSLIP DETAIL ── */}
      {viewPayslip && <PayslipDetail payslip={viewPayslip} onClose={() => setViewPayslip(null)} />}

      {/* ── CREATE DIALOG ── */}
      <Dialog open={!!createDialog} onClose={() => setCreateDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create Payslip
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setCreateDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Employee ID" fullWidth required value={createDialog?.employeeId ?? ''}
              onChange={e => setCreateDialog(p => ({ ...p, employeeId: e.target.value }))} />
            <TextField label="Period Name" fullWidth placeholder="e.g. April 2026" value={createDialog?.periodName ?? ''}
              onChange={e => setCreateDialog(p => ({ ...p, periodName: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={createDialog?.periodStartDate ?? ''} onChange={e => setCreateDialog(p => ({ ...p, periodStartDate: e.target.value }))} />
              <TextField label="End Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={createDialog?.periodEndDate ?? ''} onChange={e => setCreateDialog(p => ({ ...p, periodEndDate: e.target.value }))} />
            </Box>
            <TextField label="Pay Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={createDialog?.payDate ?? ''} onChange={e => setCreateDialog(p => ({ ...p, payDate: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Basic Salary (HKD)" type="number" fullWidth required value={createDialog?.basicSalary ?? ''}
                onChange={e => setCreateDialog(p => ({ ...p, basicSalary: Number(e.target.value) }))} />
              <TextField label="Allowances (HKD)" type="number" fullWidth value={createDialog?.totalAllowances ?? 0}
                onChange={e => setCreateDialog(p => ({ ...p, totalAllowances: Number(e.target.value) }))} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Bonus (HKD)" type="number" fullWidth value={createDialog?.totalBonus ?? 0}
                onChange={e => setCreateDialog(p => ({ ...p, totalBonus: Number(e.target.value) }))} />
              <TextField label="Deductions (HKD)" type="number" fullWidth value={createDialog?.totalDeductions ?? 0}
                onChange={e => setCreateDialog(p => ({ ...p, totalDeductions: Number(e.target.value) }))} />
            </Box>
            {createDialog?.basicSalary && (
              <Box sx={{ bgcolor: '#e8f5e9', p: 1.5, borderRadius: 1 }}>
                <Typography variant="body2">
                  <b>Gross:</b> HK${((Number(createDialog.basicSalary) + Number(createDialog.totalAllowances ?? 0) + Number(createDialog.totalBonus ?? 0))).toLocaleString()} ·{' '}
                  <b>Net:</b> HK${((Number(createDialog.basicSalary) + Number(createDialog.totalAllowances ?? 0) + Number(createDialog.totalBonus ?? 0)) - Number(createDialog.totalDeductions ?? 0)).toLocaleString()}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!createDialog?.employeeId || !createDialog?.basicSalary}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
